import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { theme } from '../theme';

/**
 * DrugInteractionAlert — checks drug-drug interactions + pregnancy safety.
 *
 * Props:
 *   medicines        — array of medicine names being prescribed
 *   patientMedicines — array from patient.currentMedicines
 *   isPregnant       — boolean (K1: triggers pregnancy-safe check)
 *   API_BASE         — base API url
 */
const DrugInteractionAlert = ({ medicines, patientMedicines, isPregnant, API_BASE }) => {
  const [interactions, setInteractions] = useState([]);
  const [pregnancyWarnings, setPregnancyWarnings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const allDrugs = [
      ...(medicines || []),
      ...(patientMedicines || []).map(m => (typeof m === 'string' ? m : m.name)).filter(Boolean),
    ];
    const prescribedOnly = (medicines || []).filter(Boolean);

    if (allDrugs.length < 2 && !isPregnant) {
      setInteractions([]);
      setPregnancyWarnings([]);
      return;
    }

    let cancelled = false;
    const check = async () => {
      setLoading(true);
      try {
        const tasks = [];
        if (allDrugs.length >= 2) {
          tasks.push(axios.post(`${API_BASE}/drug-interactions/check`, { drugs: allDrugs })
            .then(r => !cancelled && setInteractions(r.data?.interactions || r.data || []))
            .catch(() => !cancelled && setInteractions([])));
        } else if (!cancelled) {
          setInteractions([]);
        }
        if (isPregnant && prescribedOnly.length > 0) {
          tasks.push(axios.post(`${API_BASE}/drug-interactions/pregnancy-check`, { medicines: prescribedOnly, isPregnant: true })
            .then(r => !cancelled && setPregnancyWarnings(r.data?.warnings || []))
            .catch(() => !cancelled && setPregnancyWarnings([])));
        } else if (!cancelled) {
          setPregnancyWarnings([]);
        }
        await Promise.all(tasks);
      } catch (e) { /* swallowed */ }
      if (!cancelled) setLoading(false);
    };

    const timer = setTimeout(check, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [medicines, patientMedicines, isPregnant, API_BASE]);

  if (loading || (interactions.length === 0 && pregnancyWarnings.length === 0)) return null;

  const severityColors = {
    severe:   { bg: '#fef2f2', border: theme.danger,  badge: theme.danger,  badgeBg: '#fee2e2' },
    moderate: { bg: '#fff7ed', border: '#f97316',     badge: '#ea580c',     badgeBg: '#ffedd5' },
    mild:     { bg: '#fefce8', border: theme.warning,  badge: '#a16207',     badgeBg: '#fef9c3' },
  };

  const hasSevere = interactions.some(i => (i.severity || '').toLowerCase() === 'severe');
  const hasUnsafePregnancy = pregnancyWarnings.some(w => !w.safe);

  return (
    <div>
      {/* K1 — Pregnancy warnings (shown above drug interactions) */}
      {pregnancyWarnings.length > 0 && (
        <div style={{
          padding: '12px 16px', borderRadius: theme.radiusSm, marginBottom: '12px',
          backgroundColor: hasUnsafePregnancy ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${hasUnsafePregnancy ? theme.danger : theme.success}`,
          borderLeft: `4px solid ${hasUnsafePregnancy ? theme.danger : theme.success}`,
        }}>
          <div style={{ fontWeight: '700', fontSize: '0.88rem', color: hasUnsafePregnancy ? theme.danger : '#166534', marginBottom: '8px' }}>
            🤰 Pregnancy Drug Safety
          </div>
          {pregnancyWarnings.map((w, idx) => {
            const catColor = w.category === 'X' ? '#991b1b'
              : w.category === 'D' ? '#b91c1c'
              : w.category === 'C' ? '#c2410c'
              : w.category === 'B' ? '#166534'
              : '#075985';
            const catBg = w.category === 'X' || w.category === 'D' ? '#fee2e2'
              : w.category === 'C' ? '#ffedd5'
              : '#dcfce7';
            return (
              <div key={idx} style={{
                padding: '8px 12px', marginBottom: '4px',
                backgroundColor: w.safe ? '#f0fdf4' : '#fef2f2', borderRadius: '6px',
                fontSize: '0.82rem',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 3 }}>
                  <strong>{w.drugName}</strong>
                  <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: '700', backgroundColor: catBg, color: catColor }}>
                    Category {w.category}
                  </span>
                  {!w.safe && <span style={{ fontSize: '0.75rem', color: theme.danger, fontWeight: '700' }}>⚠️ AVOID</span>}
                </div>
                <div style={{ color: '#555', fontSize: '0.78rem' }}>{w.notes}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Drug-drug interactions */}
      {interactions.length > 0 && <div style={{
        padding: '12px 16px',
        borderRadius: theme.radiusSm,
        marginBottom: '12px',
        backgroundColor: hasSevere ? '#fef2f2' : '#fefce8',
        border: `1px solid ${hasSevere ? theme.danger : theme.warning}`,
        borderLeft: `4px solid ${hasSevere ? theme.danger : theme.warning}`,
      }}>
      <div style={{ fontWeight: '700', fontSize: '0.88rem', color: hasSevere ? theme.danger : '#92400e', marginBottom: '8px' }}>
        Drug Interaction Warning
      </div>
      {interactions.map((item, idx) => {
        const sev = (item.severity || 'mild').toLowerCase();
        const colors = severityColors[sev] || severityColors.mild;
        return (
          <div key={idx} style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '6px 10px', marginBottom: '4px',
            backgroundColor: colors.bg, borderRadius: '6px',
            fontSize: '0.82rem',
          }}>
            <span>{'\u26A0\uFE0F'} <strong>{item.drug1}</strong> + <strong>{item.drug2}</strong>: {item.warning || item.description || 'Potential interaction'}</span>
            <span style={{
              padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: '700',
              backgroundColor: colors.badgeBg, color: colors.badge,
              whiteSpace: 'nowrap',
            }}>
              {sev.charAt(0).toUpperCase() + sev.slice(1)}
            </span>
          </div>
        );
      })}
      </div>}
    </div>
  );
};

export default DrugInteractionAlert;
